import { dataURLtoFile, getFileExtenstionFromName } from "JS/Helpers";
import { service, useSetProfileImage } from "JS/React/Hooks/Events";
import { config } from "JS/Config";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { messaging } from "JS/Helpers/UserMessaging";
import { useNavigate } from "react-router-dom";
import { useCallback, useRef, useState } from "react";
import { captureSentryError } from "JS/Helpers/SentryHelper";
import { Content, Course, ReceivedGift } from "JS/Models";
import { mediaService } from "JS/Services/Media/Service";

export const useImageUpload = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { refetch: uploadProfileImage, loading: uploadingImage } =
        useSetProfileImage();
    const [fetchingImage, setFetchingImage] = useState<boolean>(false);
    const navigate = useNavigate();

    const uploadImageHandler = useCallback((event, userId: string) => {
        let _validFileExtensions = [
            "png",
            "gif",
            "pjp",
            "jpg",
            "pjpep",
            "jpeg",
            "jfif",
        ];
        if (
            _validFileExtensions.includes(
                getFileExtenstionFromName(event.target.files[0].name),
            )
        ) {
            return uploadProfileImage(
                event.target.files[0],
                userId,
                userId === config?.user?.memberId ? 1 : 0,
            );
        } else {
            enqueueSnackbar(messaging?.events?.selectValidFormat, {
                variant: "warning",
            });
            return null;
        }
    }, []);

    const webCamImageHandler = useCallback(
        (
            setDialogs: (
                value: React.SetStateAction<{
                    uploadImageDialog: boolean;
                    webCamImageDialog: boolean;
                }>,
            ) => void,
            dialogs: { uploadImageDialog: boolean; webCamImageDialog: boolean },
        ) => {
            let constraints = {
                audio: false,
                video: true,
            };
            return navigator.mediaDevices.getUserMedia(constraints).then(
                () => {
                    setDialogs({
                        ...dialogs,
                        webCamImageDialog: true,
                    });
                },
                (error) => {
                    if (error.name === "NotAllowedError") {
                        enqueueSnackbar(messaging?.events?.camaraError, {
                            variant: "warning",
                        });
                    }
                },
            );
        },
        [],
    );

    const captureUploadHandler = useCallback(
        async (
            setDialogs: (
                value: React.SetStateAction<{
                    uploadImageDialog: boolean;
                    webCamImageDialog: boolean;
                }>,
            ) => void,
            dialogs: { uploadImageDialog: boolean; webCamImageDialog: boolean },
            userId: string,
            capturedImage: any,
        ) => {
            let file = await dataURLtoFile(capturedImage, userId);
            if (file) {
                setDialogs({
                    ...dialogs,
                    webCamImageDialog: false,
                });
                return uploadProfileImage(
                    file,
                    userId,
                    userId === config.user.memberId ? 1 : 0,
                );
            }
        },
        [],
    );

    const closeUploadDialogHandler = useCallback(
        (
            setDialogs: (
                value: React.SetStateAction<{
                    uploadImageDialog: boolean;
                    webCamImageDialog: boolean;
                }>,
            ) => void,
            isImageExists: boolean,
            urlPath: string,
        ) => {
            setDialogs({
                uploadImageDialog: false,
                webCamImageDialog: false,
            });
            if (!isImageExists) {
                enqueueSnackbar(
                    urlPath?.includes("events")
                        ? messaging?.events?.uploadPhotoToViewTicket
                        : messaging?.fycStatus?.uploadPhoto,
                    {
                        variant: "warning",
                    },
                );
                navigate(urlPath);
            }
        },
        [],
    );

    const fetchImageUrl = useCallback(
        (
            userId: string,
            setBackgroundImageUrl: React.Dispatch<React.SetStateAction<string>>,
        ) => {
            setFetchingImage(true);
            service
                .getProfileImage(userId)
                .then((res) => {
                    if (res.imageExists && res.profileImageUrl) {
                        setBackgroundImageUrl(res.profileImageUrl);
                    }
                })
                .catch((err) =>
                    captureSentryError(err, {
                        location: `getProfileImageFromAPI`,
                    }),
                )
                .finally(() => setFetchingImage(false));
        },
        [],
    );

    return {
        uploadImageHandler,
        webCamImageHandler,
        captureUploadHandler,
        closeUploadDialogHandler,
        fetchImageUrl,
        uploadingImage,
        fetchingImage,
    };
};

export const getThumbnailUrl = async (
    postfix: string,
    fileName: string,
): Promise<string> => {
    if (!fileName) return "";
    try {
        const response = await mediaService.getThumbnailSignedUrls(postfix, [
            fileName,
        ]);
        return response.urls?.[0]?.signed_url || "";
    } catch (err) {
        console.error("Error fetching thumbnail URL:", err);
        return "";
    }
};

export const fetchBatchThumbnailUrls = async (
    items: { nid: string; postfix: string; fileName: string }[],
): Promise<Record<string, string>> => {
    const validItems = items.filter((i) => !!i.fileName);
    if (validItems.length === 0) return {};

    const groups = new Map<string, { nid: string; fileName: string }[]>();
    for (const item of validItems) {
        const group = groups.get(item.postfix) || [];
        group.push({ nid: item.nid, fileName: item.fileName });
        groups.set(item.postfix, group);
    }

    const result: Record<string, string> = {};

    const apiPromises = Array.from(groups.entries()).map(
        async ([postfix, groupItems]) => {
            try {
                const fileNames = groupItems.map((i) => i.fileName);
                const response = await mediaService.getThumbnailSignedUrls(
                    postfix,
                    fileNames,
                );

                const urlMap = new Map<string, string>();
                response.urls?.forEach((entry) =>
                    urlMap.set(entry.filename, entry.signed_url),
                );

                groupItems.forEach((item) => {
                    result[item.nid] = urlMap.get(item.fileName) || "";
                });
            } catch (err) {
                console.error("Error fetching batch thumbnail URLs:", err);
                groupItems.forEach((item) => {
                    result[item.nid] = "";
                });
            }
        },
    );

    await Promise.all(apiPromises);
    return result;
};

interface Thumbnails {
    [key: string]: string;
}

export const useDebouncedFetchThumbnails = (
    debounceMs: number = 300,
): {
    thumbnails: Thumbnails;
    refetch: (finalData: (Content | ReceivedGift | Course)[]) => void;
} => {
    const [thumbnails, setThumbnails] = useState<Thumbnails>({});
    const timerRef = useRef<ReturnType<typeof setTimeout>>(null);
    const thumbnailsRef = useRef<Thumbnails>({});
    thumbnailsRef.current = thumbnails;

    const refetch = useCallback(
        (finalData: (Content | ReceivedGift | Course)[]) => {
            if (timerRef.current) clearTimeout(timerRef.current);

            timerRef.current = setTimeout(async () => {
                const currentThumbnails = thumbnailsRef.current;
                const itemsToFetch = finalData
                    .filter(
                        (d) => !currentThumbnails || !currentThumbnails[d.nid],
                    )
                    .map((d) => ({
                        nid: d.nid,
                        postfix: d.image_url_postfix,
                        fileName: d.image_file_name,
                    }));

                if (itemsToFetch.length === 0) return;

                const newThumbnails =
                    await fetchBatchThumbnailUrls(itemsToFetch);

                setThumbnails((prev) => ({
                    ...prev,
                    ...newThumbnails,
                }));
            }, debounceMs);
        },
        [debounceMs],
    );

    return {
        thumbnails,
        refetch,
    };
};
